import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { User, Moon, BellOff, AlertTriangle, Calendar } from "lucide-react";
import { getSubscribers, getSubscriberCounts, suppressEmail, addSubscriber, deleteSubscriber, exportSubscribersCsv } from "../services/subscribersService";
import { NewsletterSubscriber, SubscriberFilters } from "../types/newsletterEdition";
import SidePanel from "./SidePanel";
import SuppressSubscribersPanel from "./SuppressSubscribersPanel";
import AddSubscribersPanel from "./AddSubscribersPanel";
import SubscribersImportModal from "./SubscribersImportModal";
import SubscriberFiltersComponent from "./SubscriberFilters";

const COLORS = {
  active:       "#44AD54", // green
  inactive:     "#F59E0B", // amber
  unsubscribed: "#718096", // grey
  bounced:      "#E53E3E", // red
};

const Subscribers: React.FC = () => {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState<NewsletterSubscriber[]>([]);
  const [counts, setCounts] = useState({
    'active': 0,
    'inactive': 0,
    'unsubscribed': 0,
    'bounced': 0,
  });
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [successMessage, setSuccessMessage] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isSuppressPanelOpen, setIsSuppressPanelOpen] = useState(false);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [filters, setFilters] = useState<SubscriberFilters>({});
  const [appliedFilters, setAppliedFilters] = useState<SubscriberFilters>({});
  const [isLoading, setIsLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // fetch paginated subscribers when currentPage or filters change
  useEffect(() => {
    setIsLoading(true);

    getSubscribers(currentPage, appliedFilters)
      .then((response) => {
        const subscribersData = response.subscribers || response;
        setSubscribers(subscribersData);
      })
      .catch((error) => {
        setErrorMessage("Failed to load subscribers. Please try again.");
        setTimeout(() => setErrorMessage(''), 5000);
        setSubscribers([]); // Clear subscribers on error
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [currentPage, appliedFilters]);

  // fetch subscriber meta information on component mount and when filters change
  useEffect(() => {
    getSubscriberCounts(appliedFilters)
      .then(({ counts, total_pages }) => {
        setCounts(counts);
        setTotalPages(total_pages);
      })
      .catch((error) => {
        setErrorMessage("Failed to load subscriber counts. Please try again.");
        setTimeout(() => setErrorMessage(''), 5000);
        // Set default counts on error
        setCounts({
          'active': 0,
          'inactive': 0,
          'unsubscribed': 0,
          'bounced': 0,
        });
        setTotalPages(0);
      });
  }, [appliedFilters]);

  // when currentPage changes, sync URL param
  useEffect(() => {
    const pageParam = Number(searchParams.get('page')) || 1;
    if (pageParam !== currentPage) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('page', currentPage.toString());
        return newParams;
      });
    }
  }, [currentPage]);

  // when URL param changes, sync currentPage
  useEffect(() => {
    const rawPage = Number(searchParams.get('page')) || 1;
    const paramPage = Math.max(1, Math.min(rawPage, totalPages)); // clamp between 1 and totalPages

    if (paramPage !== currentPage) {
      setCurrentPage(paramPage);
    }
  }, [searchParams, totalPages]);

  const refreshSubscribers = async (page: number) => {
    setIsLoading(true);
    try {
      const [subscribersResponse, countsResponse] = await Promise.all([
        getSubscribers(page, appliedFilters),
        getSubscriberCounts(appliedFilters)
      ]);

      const subscribersData = subscribersResponse.subscribers;
      setSubscribers(subscribersData);
      setCounts(countsResponse.counts);
      setTotalPages(countsResponse.total_pages);
    } catch (error) {
      setErrorMessage("Failed to refresh data. Please try again.");
      setTimeout(() => setErrorMessage(''), 5000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyFilters = () => {
    const newFilters = { ...filters };
    setAppliedFilters(newFilters);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFilters({});
    setAppliedFilters({});
    setCurrentPage(1);
  };

  const getVisiblePages = () => {
    // maximum number of page buttons to show at once
    const maxVisible = 5;

    // simply show all pages if total pages <= max visible
    if (totalPages <= maxVisible) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    // center the visible pages around the currentPage as much as possible
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = startPage + maxVisible - 1;

    // clamp the last page to totalPages
    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = endPage - maxVisible + 1;
    }

    // create an array of visible page numbers from start to end (inclusive)
    return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
  };

  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'Active':
        return { backgroundColor: "#d1fae5", color: "#065f46" }; // green
      case 'Inactive':
        return { backgroundColor: "#fef3c7", color: "#92400e" }; // amber
      case 'Unsubscribed':
        return { backgroundColor: "#e5e7eb", color: "#374151" }; // gray
      case 'Bounced':
        return { backgroundColor: "#fee2e2", color: "#dc2626" }; // red
      default:
        return { backgroundColor: "#e5e7eb", color: "#374151" }; // gray
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Active':
        return <User size={16} />;
      case 'Inactive':
        return <Moon size={16} />;
      case 'Unsubscribed':
        return <BellOff size={16} />;
      case 'Bounced':
        return <AlertTriangle size={16} />;
      default:
        return <User size={16} />;
    }
  };

  const handleUnsubscribe = async (email: string) => {
    try {
      if (!email) return;
      const result = await suppressEmail(email);

      // Update the subscriber in the list with new status
      setSubscribers(subscribers.map(sub =>
        sub.email === email ? result.subscriber : sub
      ));

      // Populate search and apply filter to highlight the unsubscribed user
      setFilters({ ...filters, search: email });
      setAppliedFilters({ ...appliedFilters, search: email });
      setCurrentPage(1);

      setSuccessMessage('Successfully unsubscribed email');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsSuppressPanelOpen(false);
    } catch (e: any) {
      if (e.response?.status === 404) {
        // Subscriber doesn't exist - show as info
        setInfoMessage(e.response?.data?.error || 'Subscriber does not exist');
        setTimeout(() => setInfoMessage(''), 3000);
        setIsSuppressPanelOpen(false);
      } else {
        setErrorMessage(e.response?.data?.error || 'An error occurred when unsubscribing user');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    }
  };

  const handleDelete = async (email: string) => {
    try {
      if (!email) return;
      await deleteSubscriber(email);

      // Clear search since subscriber is gone
      setFilters({ ...filters, search: '' });
      setAppliedFilters({ ...appliedFilters, search: '' });
      await refreshSubscribers(currentPage);

      setSuccessMessage('Successfully removed subscriber');
      setTimeout(() => setSuccessMessage(''), 3000);
      setIsSuppressPanelOpen(false);
    } catch (e: any) {
      if (e.response?.status === 404) {
        // Subscriber doesn't exist - show as info
        setInfoMessage(e.response?.data?.error || 'Subscriber does not exist');
        setTimeout(() => setInfoMessage(''), 3000);
        setIsSuppressPanelOpen(false);
      } else {
        setErrorMessage(e.response?.data?.error || 'An error occurred when removing subscriber');
        setTimeout(() => setErrorMessage(''), 3000);
      }
    }
  };

  const handleAddSubscriber = async (
    email: string,
    customFields?: Record<string, string | number | boolean | null>,
    bypassValidation?: boolean,
    skipWelcomeEmail?: boolean
  ): Promise<{ success: boolean; error?: string }> => {
    try {
      if (!email) return { success: false };
      const result = await addSubscriber(email, customFields, bypassValidation, skipWelcomeEmail);

      // Populate search and apply filter to highlight the subscriber
      setFilters({ ...filters, search: email });
      setAppliedFilters({ ...appliedFilters, search: email });
      setCurrentPage(1);

      if (result.message) {
        // Duplicate - show as info
        setInfoMessage(result.message);
        setTimeout(() => setInfoMessage(''), 3000);
      } else {
        // New subscriber - append to list
        setSubscribers([result.subscriber, ...subscribers]);
        setSuccessMessage('Successfully added subscriber');
        setTimeout(() => setSuccessMessage(''), 3000);
      }

      // Close the panel after success
      setIsAddPanelOpen(false);
      return { success: true };
    } catch (e: any) {
      console.log("Failed to add subscriber:", e);
      const errorMsg = e.response?.data?.error || "An error occurred when adding subscriber";
      // Return error to panel, don't close panel
      return { success: false, error: errorMsg };
    }
  };

  return (
    <div>
      <div style={{
        filter: isImportModalOpen ? "blur(4px)" : "none",
        pointerEvents: isImportModalOpen ? "none" : "auto",
      }}>
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
        }}>
          <h1>Subscribers</h1>

        <div style={{ display: "inline-block", marginLeft: "auto" }}>
          <button
            className="tertiary-info-button"
            onClick={() => setIsSuppressPanelOpen(true)}
            style={{
              cursor: "pointer",
              marginRight: "8px",
              width: "82px",
            }}
          >
            Remove
          </button>
          <button
            className="tertiary-info-button"
            onClick={() => setIsAddPanelOpen(true)}
            style={{
              cursor: "pointer",
              marginRight: "8px",
              width: "82px",
            }}
          >
            Add
          </button>
          <button
            className="tertiary-info-button"
            disabled={exporting}
            onClick={async () => {
              setExporting(true);
              try {
                await exportSubscribersCsv(appliedFilters);
              } catch (e) {
                console.error("Failed to export subscribers:", e);
              } finally {
                setExporting(false);
              }
            }}
            style={{
              cursor: exporting ? "not-allowed" : "pointer",
              width: "82px",
              marginRight: "8px",
            }}
          >
            {exporting ? "Exporting..." : "Export"}
          </button>
          <button
            className="tertiary-info-button"
            onClick={() => setIsImportModalOpen(true)}
            style={{
              cursor: "pointer",
              width: "82px",
              marginRight: "8px",
            }}
          >
            Upload
          </button>
          <button
            className="tertiary-info-button"
            onClick={() => navigate("/subscribers/custom-fields")}
            style={{
              cursor: "pointer",
              whiteSpace: "nowrap",
              paddingLeft: "16px",
              paddingRight: "16px",
            }}
          >
            Manage Fields
          </button>
        </div>
      </div>

      <div
        style={{
          marginTop: "8px",
          marginBottom: "20px",
          textAlign: "center",
          fontSize: "0.95rem",
          color: "#333",
        }}
      >
        <p style={{ margin: 0, fontWeight: "bold" }}>
          <span style={{ color: COLORS.active }}>{counts.active}</span> active,{" "}
          <span style={{ color: COLORS.inactive }}>{counts.inactive}</span> inactive,{" "}
          <span style={{ color: COLORS.unsubscribed }}>{counts.unsubscribed}</span> unsubscribed,{" "}and{" "}
          <span style={{ color: COLORS.bounced }}>{counts.bounced}</span> bounced
        </p>
      </div>

      { successMessage &&
        <div style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "12px"
        }}>
          <div style={{
            backgroundColor: "#e0f7df",
            color: "#2e7d32",
            border: "1px solid #2e7d32",
            borderRadius: "6px",
            padding: "4px 10px",
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.9em",
            height: "32px"
          }}>
            ✅
            <span style={{marginLeft: "8px"}}>{successMessage}</span>
          </div>
        </div>
      }
      { infoMessage &&
        <div style={{
          display: "flex",
          justifyContent: "center",
          marginBottom: "12px"
        }}>
          <div style={{
            backgroundColor: "#f0f0f0",
            color: "#555",
            border: "1px solid #888",
            borderRadius: "6px",
            padding: "4px 10px",
            display: "inline-flex",
            alignItems: "center",
            fontSize: "0.9em",
            height: "32px"
          }}>
            {infoMessage}
          </div>
        </div>
      }
      { errorMessage && <div style={{ color: 'red', marginBottom: "12px" }}>{errorMessage}</div>}

      <SubscriberFiltersComponent
        filters={filters}
        onFiltersChange={(newFilters) => {
          setFilters(newFilters);
        }}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
      />

      {isLoading && (
        <div style={{
          textAlign: "center",
          padding: "20px",
          color: "#666",
          backgroundColor: "#f9fafb",
          borderRadius: "8px",
          margin: "20px 0"
        }}>
          <p>Loading subscribers...</p>
        </div>
      )}
      {!isLoading && (
      <ul
        className="newsletter-list"
      >
        { subscribers.length === 0 ? (
          <div style={{
            textAlign: "center",
            padding: "40px",
            color: "#666",
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            margin: "20px 0"
          }}>
            <h3 style={{ marginBottom: "8px" }}>No subscribers found</h3>
            <p>
              {Object.keys(appliedFilters).length > 0
                ? "Try adjusting your filters or clear them to see all subscribers."
                : "Start by uploading your subscriber list."}
            </p>
          </div>
        ) : subscribers.map((subscriber: NewsletterSubscriber) => (
          <li
            key={subscriber.id}
            className="newsletter-item"
          >
            <Link
              to={`/subscribers/${subscriber.id}`}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                width: '100%',
                padding: 'inherit'
              }}
            >
              <div className="newsletter-item-content-left">
                <span className="newsletter-title">
                  {subscriber.email}
                </span>
              </div>

              <div style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}>
                <span
                  className="newsletter-status"
                  style={getStatusStyle(subscriber.status!)}
                >
                  <span className="newsletter-status-icon">
                    {getStatusIcon(subscriber.status!)}
                  </span>
                  <span>{subscriber.status}</span>
                </span>

                {(subscriber.status === 'Unsubscribed' || subscriber.status === 'Bounced') && subscriber.suppressed_at ? (
                  <span
                    className="newsletter-status"
                    style={{ backgroundColor: "#e5e7eb", color: "#374151", marginRight: "12px" }}
                  >
                    <span className="newsletter-status-icon">
                      <Calendar size={16} />
                    </span>
                    <span>{subscriber.status}: {new Date(subscriber.suppressed_at).toLocaleDateString()}</span>
                  </span>
                ) : (subscriber.status === 'Active' || subscriber.status === 'Inactive') ? (
                  <span
                    className="newsletter-status"
                    style={{ backgroundColor: "#e5e7eb", color: "#374151", marginRight: "12px" }}
                  >
                    <span className="newsletter-status-icon">
                      <Calendar size={16} />
                    </span>
                    <span>Last updated: {new Date(subscriber.updated_at).toLocaleDateString()}</span>
                  </span>
                ) : (
                  <span
                    className="newsletter-status"
                    style={{ backgroundColor: "#e5e7eb", color: "#374151", marginRight: "12px" }}
                  >
                    <span className="newsletter-status-icon">
                      <Calendar size={16} />
                    </span>
                    <span>Added: {new Date(subscriber.created_at).toLocaleDateString()}</span>
                  </span>
                )}
              </div>
            </Link>
          </li>
        ))}
      </ul>
      )}

      <div style={{ textAlign: "center", margin: "20px 0" }}>
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="subscriber-pagination-end-button"
          style={{
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          &lt;&lt;
        </button>
        <button
          onClick={() => setCurrentPage(currentPage - 1)}
          disabled={currentPage === 1}
          className="subscriber-pagination-end-button"
          style={{
            cursor: currentPage === 1 ? "not-allowed" : "pointer",
          }}
        >
          &lt;
        </button>

        {getVisiblePages().map((pageNum) => (
          <button
            key={pageNum}
            onClick={() => setCurrentPage(pageNum)}
            className="subscriber-pagination-number-button"
            style={{
              // potions purple highlighting TODO: defined the Accent color
              backgroundColor: currentPage === pageNum ? "#5d5ded" : "#fff",
              color: currentPage === pageNum ? "#fff" : "#5d5ded",
            }}
          >
            {pageNum}
          </button>
        ))}
        <button
          onClick={() => setCurrentPage(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="subscriber-pagination-end-button"
          style={{
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          &gt;
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="subscriber-pagination-end-button"
          style={{
            cursor: currentPage === totalPages ? "not-allowed" : "pointer",
          }}
        >
          &gt;&gt;
        </button>
      </div>
      <SidePanel isOpen={isSuppressPanelOpen} onClose={() => setIsSuppressPanelOpen(false)}>
        <SuppressSubscribersPanel handleUnsubscribe={handleUnsubscribe} handleDelete={handleDelete}></SuppressSubscribersPanel>
      </SidePanel>
      <SidePanel isOpen={isAddPanelOpen} onClose={() => setIsAddPanelOpen(false)}>
        <AddSubscribersPanel handleAddSubscriber={handleAddSubscriber}></AddSubscribersPanel>
      </SidePanel>
      </div>

      {/* Import Modal */}
      {isImportModalOpen && (
        <SubscribersImportModal
          onClose={() => setIsImportModalOpen(false)}
          onSuccess={() => {
            refreshSubscribers(1);
            setSuccessMessage('We\'re processing your list');
            setTimeout(() => setSuccessMessage(''), 3000);
          }}
        />
      )}
    </div>
  )
};

export default Subscribers;
